import styled, { css } from 'styled-components'
import { color, space, typography, layout } from 'styled-system'
import ButtonBase from '@mui/material/ButtonBase'
import { darken } from '@mui/material/styles'

import { responsiveProperty } from 'styles/utils'

import { ButtonSize, btnSizes } from './Button.sizes'
import { ButtonColor, btnColors } from './Button.variants'

export const buttonCss = css<{ $size: ButtonSize; $color: ButtonColor; $isLoading: boolean }>`
  appearance: none;
  position: relative;
  padding: 0.5em 1em;
  font-weight: 500;
  line-height: 1.25;
  cursor: ${({ $isLoading }) => ($isLoading ? 'progress' : 'pointer')};
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-decoration: none;
  text-transform: none;
  transition-duration: ${({ theme }) => theme.transitions.duration.shorter}ms;
  border-radius: 4px;
  box-shadow: none;
  fill: currentColor;
  border: none;
  width: fit-content;

  &:hover {
    text-decoration: none;
    box-shadow: 0 1px 4px -1px rgb(0 0 0 / 20%), 0px 2px 3px 0px rgb(0 0 0 / 7%),
      0px 1px 3px 0px rgb(0 0 0 / 12%);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
    filter: none;
    pointer-events: none;
  }

  ${({ $size }) => {
    const { maxHeight, minHeight, ...otherProps } = btnSizes[$size]
    return {
      ...responsiveProperty({
        max: maxHeight,
        min: minHeight,
        cssProperty: 'height',
        unit: 'rem',
      }),
      ...otherProps,
    }
  }};

  ${({ $color, theme }) => {
    const { color, background, hoverBg, border } = btnColors(theme)[$color]

    return {
      border: border ? `1px solid ${border}` : '',
      color,
      background,
      '&:hover': {
        background: hoverBg ?? darken(background, 0.2),
      },
    }
  }};

  .btn-icon-start {
    margin-right: 0.75em;
  }

  .btn-icon-end {
    margin-left: 0.75em;
  }
`

export const ButtonStyled = styled(ButtonBase)`
  ${buttonCss};
  ${typography};
  ${color};
  ${space};
  ${layout};
`
